'use client';
import { BoatCardImage } from '@components/BoatCardImage';
import { FavoritablePublicBoatCard } from '@components/FavoritablePublicBoatCard';
import { canonicalSlug } from '@utils/canonicalSlug';
import { getBoatName } from '@utils/getBoatName';
import { useSize } from 'ahooks';
import { Grid, Space } from 'antd';
import cn from 'classnames';
import { useKeenSlider } from 'keen-slider/react';
import { ChevronRight } from 'lucide-react';
import Link from 'next/link';
import { FC, useMemo, useRef, useState } from 'react';
import { CarouselControllersTop } from 'sailor-ui/CarouselControllersTop';
import styles from './ExplorerBoatsCarousel.module.scss';
import { ExplorerBoatsCarouselProps } from './ExplorerBoatsCarousel.props';

const { useBreakpoint } = Grid;
const ExplorerBoatsCarousel: FC<ExplorerBoatsCarouselProps> = ({ subtitle, more, title, badge, boats }) => {
  const refSliderWrapper = useRef(null);
  const size = useSize(refSliderWrapper);

  const [currentSlide, setCurrentSlide] = useState(0);
  const [loaded, setLoaded] = useState(false);

  const { md } = useBreakpoint();

  const [ref, instanceRef] = useKeenSlider<HTMLDivElement>({
    loop: false,
    mode: 'snap',
    slides: {
      perView: 'auto',
      spacing: 0,
    },
    initial: 0,
    slideChanged(slider) {
      setCurrentSlide(slider.track.details.rel);
    },
    created() {
      setLoaded(true);
    },
  });

  const nrOfDots = useMemo(() => {
    if (boats?.length) {
      if (size?.width) {
        const itemsPerPage = Math.ceil(size?.width / 270);

        if (boats?.length > itemsPerPage) {
          return boats?.length - itemsPerPage + 2;
        }
      }
    }

    return 0;
  }, [boats?.length, size]);

  return (
    <section className={styles.container} ref={refSliderWrapper}>
      <header className={styles.carouselHeader}>
        <div>
          <div className={styles.contentTitle}>
            {/*
              A kicker label, rendered above the section's own `<h2>`. As an `<h3>` it put the
              heading levels out of order (h3 before h2) on /boats, /boats/type, /boats/hull-type,
              /boats/activity and the homepage — see MDC-4221.
            */}
            <p className={`${styles.moreText}`}>{subtitle}</p>
            {badge && <div className={styles.badge}>{badge}</div>}
          </div>

          <Space align="baseline" size={15} direction={md ? 'horizontal' : 'vertical'}>
            <h2>{title}</h2>
            {/*
              Six activity carousels linked to six different pages under the anchor text "Explore
              all". Appending the facet name gives each link a distinct anchor text and accessible
              name without repeating, on screen, the heading it sits beside.
            */}
            <Link href={more.url} className={styles.moreLink}>
              {more.text} <span className="visually-hidden">{title}</span> <ChevronRight size={16} />
            </Link>
          </Space>
        </div>
        {loaded && instanceRef.current && !!nrOfDots && (
          <CarouselControllersTop currentSlide={currentSlide} instanceRef={instanceRef} nrOfDots={nrOfDots} />
        )}
      </header>

      <div ref={ref} className="keen-slider">
        {boats.map((item) => (
          <Link
            className={cn('keen-slider__slide', styles.boatCardWrapper)}
            href={`/boat/${canonicalSlug(item.slug.current)}`}
            key={item._id}
          >
            <FavoritablePublicBoatCard
              sanityBoatId={item._id}
              className={styles.publicBoatCardStyle}
              title={getBoatName(item)}
              imageComponent={<BoatCardImage image={item.image} height={200} width={300} />}
              boatModel={item.model}
              views={item.viewsCount}
            />
          </Link>
        ))}
        {/* <div className="keen-slider__slide number-slide1">1</div>
        <div className="keen-slider__slide number-slide2">2</div>
        <div className="keen-slider__slide number-slide3">3</div>
        <div className="keen-slider__slide number-slide4">4</div>
        <div className="keen-slider__slide number-slide5">5</div>
        <div className="keen-slider__slide number-slide6">6</div> */}
      </div>
    </section>
  );
};

export { ExplorerBoatsCarousel };
